import { createElement, useState, useRef, useCallback, AnimationEvent, useEffect } from 'react';
import ReactDOM from 'react-dom';
import FocusLock from 'react-focus-lock';
import eventBus, { Listener } from '@sas/nova-core/eventBus';
import { useDesignTokens, renderInlineCssVars, useGlobalDesignTokens } from '../../theme';
import { ModalAnimation, ModalEventName, ModalProps } from './';
import { useOnOutsideMouseDown } from '../../hooks/useOnOutsideMouseDown';
import { PopoverRootProvider, usePopoverRoot } from '../../hooks/usePopoverRoot';
import { ModalA11YProvider, useModalA11YContext } from './ModalA11YProvider';
import { ReactFocusLockProps } from 'react-focus-lock/interfaces';

import cx from 'classnames';
import cn from './Modal.css.json';
import './Modal.css';

// Store this object to make sure FocusLock's returnFocus prop doesn't change when re-rendered
// Otherwise, it will trigger unexpected focus() calls.
const focusLockFocusOptions: ReactFocusLockProps['returnFocus'] = { preventScroll: true };

/**
 * Used to fire custom Modal Events
 * for now we just have one event "modal:disableFocusLockNonModal"
 */
export const publishModalEvent = (eventName: ModalEventName) => {
  eventBus.publish(eventName);
};

const subscribeModalEvent = (eventName: ModalEventName, listener: Listener) => {
  eventBus.subscribe(eventName, listener);
};

const unsubscribeModalEvent = (eventName: ModalEventName, listener: Listener) => {
  eventBus.unsubscribe(eventName, listener);
};

export const Modal = ({ showBlockLayer = true, hideAnimation = true, ...props }: ModalProps) => {
  const dt = useDesignTokens();
  const globalDesignTokens = useGlobalDesignTokens();

  const cssVars = renderInlineCssVars(
    {
      bg: dt.themeVariant === 'light' ? dt.c.white60 : dt.c.black60,
      'anim-dur-enter': dt.animation.duration.enter.m,
      'anim-tf-enter': dt.animation.timingFunction.enter.m,
      'anim-dur-exit': dt.animation.duration.exit.m,
      'anim-tf-exit': dt.animation.timingFunction.exit.m,
    },
    'modal'
  );

  const popoverRoot = usePopoverRoot();
  const [childPopoverRoot, setChildPopoverRoot] = useState<Element | null>(null);
  const [isFocusLockDisabled, disableFocusLock] = useState(false);
  const modalDivRef = useRef<HTMLDivElement>(null);
  const onOutsideClickHandler = useCallback(() => disableFocusLock(!showBlockLayer), [
    showBlockLayer,
  ]);

  useOnOutsideMouseDown(modalDivRef, onOutsideClickHandler, true);

  useEffect(() => {
    // if showBlockLayer is false this is a non-modal
    if (!showBlockLayer) {
      const disableFocusLockNonModal = () => {
        disableFocusLock(true);
      };

      subscribeModalEvent('modal:disableFocusLockNonModal', disableFocusLockNonModal);

      return () => {
        unsubscribeModalEvent('modal:disableFocusLockNonModal', disableFocusLockNonModal);
      };
    }
  }, [showBlockLayer]);

  const [{ prevIsOpen, opening, closing }, setAnimation] = useState<ModalAnimation>({
    elementAcceptsAnimation: false,
    prevIsOpen: props.isOpen,
    opening: !hideAnimation,
    closing: false,
  });

  if (props.isOpen !== prevIsOpen) {
    setAnimation((prev) => {
      return {
        ...prev,
        prevIsOpen: props.isOpen,
        opening: !hideAnimation && !prevIsOpen,
        closing: !hideAnimation && prev.elementAcceptsAnimation && prevIsOpen,
      };
    });
  }

  const publishOpenState = useModalA11YContext();

  useEffect(() => {
    if (showBlockLayer) {
      publishOpenState(props.isOpen);
      return () => {
        // this is specifically for the BaseDialog implementations (FullScreenDialog)
        // which unmounts the BaseDialog on closing.
        publishOpenState(false);
      };
    }
  }, [props.isOpen, showBlockLayer, publishOpenState]);

  if ((!props.isOpen && !closing) || popoverRoot === null) {
    return null;
  } else {
    return ReactDOM.createPortal(
      <div
        className={cx(
          cn.container,
          showBlockLayer ? cn.modalBlockLayer : cn.nonmodalBlockLayer,
          opening && cn.opening,
          closing && cn.closing
        )}
        style={{
          ...cssVars,
          ...globalDesignTokens,
        }}
        onContextMenu={props.onContextMenu}
        ref={modalDivRef}
        {...(!showBlockLayer && {
          onFocus: (e) => {
            if (e.relatedTarget && !popoverRoot.contains(e.relatedTarget as Node)) {
              disableFocusLock(false);
            }
          },
          onMouseDown: () => disableFocusLock(false),
        })}
        onAnimationEnd={(event: AnimationEvent) => {
          if (event.target === event.currentTarget) {
            if (opening) {
              setAnimation((prev) => {
                const state = { ...prev, elementAcceptsAnimation: true, opening: false };
                return state;
              });
            }
            if (closing) {
              setAnimation((prev) => {
                return { ...prev, prevIsOpen: false, opening: false, closing: false };
              });
            }
          }
        }}
        data-testid={props.testId}
        id={props.id}
        data-is-modal={showBlockLayer ? true : undefined}
      >
        {/* TODO: we probably need popper to use fixed positioning and give up on absolute to support this nesting correctly. */}
        <PopoverRootProvider value={showBlockLayer ? childPopoverRoot : popoverRoot}>
          <FocusLock
            {...(!showBlockLayer && {
              className: cn.nonmodalBlockContent,
            })}
            {...(!showBlockLayer && !opening && !closing && { disabled: isFocusLockDisabled })}
            returnFocus={focusLockFocusOptions}
            {...props.focusLockProps}
          >
            {showBlockLayer ? (
              <ModalA11YProvider>{props.children}</ModalA11YProvider>
            ) : (
              props.children
            )}
          </FocusLock>
        </PopoverRootProvider>
        {showBlockLayer && <div ref={setChildPopoverRoot} />}
      </div>,
      popoverRoot
    );
  }
};
